import { useId, useRef, type KeyboardEvent } from 'react'
import { useSearchParams } from 'react-router'
import { PageHeader } from '@/features/preinvest/components/PageHeader'
import { cn } from '@/lib/utils'
import { CategoryTable } from './CategoryTable'
import { LabelTable } from './LabelTable'

const TABS = [
  {
    key: 'relatie',
    label: 'Relatiecategorieën',
    intro:
      'Een relatiecategorie zegt wat voor relatie een contact of organisatie is. Elke relatie heeft er hooguit één.',
  },
  {
    key: 'afspraak',
    label: 'Soorten afspraken',
    intro:
      'De soorten afspraken die je met een relatie vastlegt. In gebruik zodra er afspraken zijn.',
  },
  {
    key: 'lijst',
    label: 'Soorten lijsten',
    intro: 'Soorten lijsten, zoals events en mailings. In gebruik zodra lijsten een soort krijgen.',
  },
  {
    key: 'labels',
    label: 'Labels',
    intro: 'Vrije kenmerken met een kleur. Een relatie kan meerdere labels hebben.',
  },
] as const

type TabKey = (typeof TABS)[number]['key']

/** Where an arrow key, Home or End takes the focus, or null for any other key. */
function targetTab(key: string, index: number): number | null {
  switch (key) {
    case 'ArrowRight':
      return (index + 1) % TABS.length
    case 'ArrowLeft':
      return (index - 1 + TABS.length) % TABS.length
    case 'Home':
      return 0
    case 'End':
      return TABS.length - 1
    default:
      return null
  }
}

/**
 * Categorieën en labels (spec-63, design §4.8): one tab per category scope plus
 * labels, the tab in `?tab=` (IA-9). Everyone with CRM access manages them.
 *
 * The tabs follow the ARIA tabs pattern: one tab stop (the active tab), the arrow keys,
 * Home and End move to a tab and open it, and the content is a tabpanel named by its tab.
 */
export function SettingsPage() {
  const [params, setParams] = useSearchParams()
  const active = TABS.find((tab) => tab.key === params.get('tab')) ?? TABS[0]
  const tabs = useRef(new Map<TabKey, HTMLButtonElement>())
  const id = useId()
  const tabId = (key: TabKey) => `${id}-tab-${key}`
  const panelId = `${id}-panel`

  function open(key: TabKey) {
    setParams(key === 'relatie' ? {} : { tab: key })
  }

  function onKeyDown(event: KeyboardEvent, index: number) {
    // Alt+Left and Cmd+Left are the browser's "back": a modified key is not ours.
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
    const target = targetTab(event.key, index)
    if (target === null) return
    event.preventDefault()
    const { key } = TABS[target]
    open(key)
    tabs.current.get(key)?.focus()
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Categorieën en labels"
        subtitle="De waarden die het hele team gebruikt. Wijzigingen gelden meteen overal."
      />
      <section className="vonk-card flex flex-col gap-4">
        <div
          role="tablist"
          aria-label="Soort waarden"
          className="flex flex-wrap gap-1 border-b border-border"
        >
          {TABS.map((tab, index) => (
            <button
              key={tab.key}
              ref={(node) => {
                if (node) tabs.current.set(tab.key, node)
                else tabs.current.delete(tab.key)
              }}
              id={tabId(tab.key)}
              type="button"
              role="tab"
              aria-selected={tab.key === active.key}
              aria-controls={tab.key === active.key ? panelId : undefined}
              tabIndex={tab.key === active.key ? 0 : -1}
              onClick={() => open(tab.key)}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={cn(
                '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                tab.key === active.key
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div
          role="tabpanel"
          id={panelId}
          aria-labelledby={tabId(active.key)}
          className="flex flex-col gap-4"
        >
          <p className="text-sm text-muted-foreground">{active.intro}</p>
          {active.key === 'labels' ? (
            <LabelTable />
          ) : (
            <CategoryTable key={active.key} scope={active.key} />
          )}
        </div>
      </section>
    </div>
  )
}
